import React from "react";

import { useTranslation } from "react-i18next";

import { Badge } from "@/src/components/ui/badge";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Skeleton } from "@/src/components/ui/skeleton";
import { cn } from "@/src/lib/utils";

import { formatCount, formatDuration } from "../charts/charts.utils";

import type { UseQueryResult } from "@tanstack/react-query";
import type { JSX, ReactNode } from "react";

export const EMPTY_VALUE = "—";

export function formatCountOrEmpty(
  value: number | null | undefined,
  lang: string,
): string {
  return value == null ? EMPTY_VALUE : formatCount(value, lang);
}

export function formatDurationOrEmpty(
  seconds: number | null | undefined,
): string {
  return seconds == null ? EMPTY_VALUE : formatDuration(Math.max(0, seconds));
}

type StatScope = "asOfToday" | "period";

export type QueryStatus = Pick<
  UseQueryResult,
  "isLoading" | "isFetching" | "isError"
>;

interface StatCardProps {
  label: string;
  value: ReactNode;
  scope?: StatScope;
  footer?: ReactNode;
  query?: QueryStatus;
  valueTitle?: string;
}

function StatCard({
  label,
  value,
  scope,
  footer,
  query,
  valueTitle,
}: StatCardProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { isLoading, isFetching, isError } = query ?? {};
  const displayedValue = isError ? EMPTY_VALUE : value;

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardDescription>{label}</CardDescription>
        <CardTitle
          title={valueTitle}
          className={cn(
            "line-clamp-2 text-2xl leading-tight font-semibold break-words transition-opacity",
            isFetching && "opacity-50",
          )}
        >
          {isLoading ? <Skeleton className="h-8 w-20" /> : displayedValue}
        </CardTitle>
        {scope ? (
          <Badge className="justify-self-start" variant="neutral">
            {t(`charts.scope.${scope}`)}
          </Badge>
        ) : null}
      </CardHeader>
      {footer && !isError ? (
        <CardFooter className="text-muted-foreground px-4 text-sm">
          {footer}
        </CardFooter>
      ) : null}
    </Card>
  );
}

export default StatCard;
